import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { configApi } from '../api/config';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { StatusBadge } from '../components/StatusBadge';
import { useAsyncData } from '../hooks/useAsyncData';
import { demoFeaturesEnabled } from '../runtime-features';

const SETTINGS_STORAGE_KEY = 'oss-console-ux-settings';

interface ConsoleUxSettings {
  showNextActions: boolean;
  collapseAdvancedEditors: boolean;
}

const defaultUxSettings: ConsoleUxSettings = { showNextActions: true, collapseAdvancedEditors: true };

function readStoredUxSettings(): ConsoleUxSettings {
  try {
    return { ...defaultUxSettings, ...(JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) ?? '{}') as Partial<ConsoleUxSettings>) };
  } catch {
    return defaultUxSettings;
  }
}

export function readConsoleUxSettings(): ConsoleUxSettings {
  return readStoredUxSettings();
}

export function SettingsConsolePage() {
  const { search } = useLocation();
  const showDemoFeatures = demoFeaturesEnabled();
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [uxSettings, setUxSettings] = useState<ConsoleUxSettings>(readStoredUxSettings);
  const { data: demoData, refresh } = useAsyncData(() => configApi.maintenance.demoData(), []);
  const { data: implementations } = useAsyncData(() => configApi.capabilities.documentImplementations(), []);
  const { data: compliancePosture } = useAsyncData(() => configApi.security.compliancePosture(), []);
  const { data: monitoringSignals } = useAsyncData(() => configApi.security.monitoringSignals(), []);

  function updateUxSetting(key: keyof ConsoleUxSettings, value: boolean) {
    const next = { ...uxSettings, [key]: value };
    setUxSettings(next);
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(next));
  }

  async function runQuarantineDemoData() {
    setBusy(true);
    setMaintenanceError(null);
    try {
      await configApi.maintenance.quarantineDemoData();
      await refresh();
    } catch (caught) {
      setMaintenanceError(caught instanceof Error ? caught.message : 'Demo-data quarantine failed.');
    } finally {
      setBusy(false);
    }
  }

  function requestQuarantineDemoData() {
    setConfirmDialog({
      title: 'Quarantine demo data',
      message: 'Archive demo Integration partners and deactivate unlinked AS2 test profiles. Historical documents will be preserved.',
      confirmLabel: 'Quarantine',
      risk: 'high',
      requireText: 'QUARANTINE',
      onConfirm: runQuarantineDemoData,
    });
  }

  return (
    <div className="page-stack">
      <section className="hero">
        <div>
          <span>SETTINGS</span>
          <h1>Console Settings</h1>
          <p>Govern advanced admin access, expert JSON editors, retention defaults, and environment posture while keeping old pages reachable.</p>
        </div>
      </section>
      <section className="page-grid two-columns">
        <div className="panel">
          <h2>Guided UX defaults</h2>
          <div className="stack-list">
            <label className="inline-check">
              <input
                type="checkbox"
                checked={uxSettings.showNextActions}
                onChange={(event) => updateUxSetting('showNextActions', event.target.checked)}
              />
              Show next action prompts
            </label>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={uxSettings.collapseAdvancedEditors}
                onChange={(event) => updateUxSetting('collapseAdvancedEditors', event.target.checked)}
              />
              Keep advanced JSON editors collapsed by default
            </label>
            <div className="stack-item">
              <strong>CUSCAR demonstration</strong>
              <span>{showDemoFeatures ? 'Enabled by VITE_SHOW_DEMO_FEATURES.' : 'Disabled for this deployment.'}</span>
            </div>
          </div>
        </div>
        <div className="panel">
          <h2>Advanced / Legacy Admin</h2>
          <div className="stack-list">
            <GuidedAction title="Partners CRUD" detail="Existing raw partner configuration page." to="/config/partners" search={search} />
            <GuidedAction title="Connector Instances" detail="Configured outbound endpoints that flows attach during target setup." to="/config/connector-health" search={search} />
            <GuidedAction title="Connector Definitions" detail="Reusable transport driver catalog for REST, webhook, SFTP, and Kafka." to="/config/connectors" search={search} />
            <GuidedAction title="Mappings CRUD" detail="Existing mapping rules API editor." to="/config/mappings" search={search} />
            <GuidedAction title="Validation Rules" detail="Existing structural and semantic validation rules." to="/config/validation-rules" search={search} />
          </div>
        </div>
      </section>
      <section className="panel">
        <h2>Development-data inventory</h2>
        <p>Archive and deactivate known demonstration records without deleting historical documents or audit evidence.</p>
        <dl className="detail-list">
          <dt>Integration demo partners</dt><dd>{demoData?.partners.length ?? 0}</dd>
          <dt>Historical demo documents</dt><dd>{demoData?.partners.reduce((total, partner) => total + partner.documentCount, 0) ?? 0}</dd>
          <dt>Unlinked AS2 test profiles</dt><dd>{demoData?.as2TransportProfiles.length ?? 0}</dd>
        </dl>
        {maintenanceError ? <div className="error-banner">{maintenanceError}</div> : null}
        <button type="button" disabled={busy || !demoData || (demoData.partners.length === 0 && demoData.as2TransportProfiles.length === 0)} onClick={requestQuarantineDemoData}>
          {busy ? 'Quarantining...' : 'Quarantine demo data'}
        </button>
      </section>
      <section className="page-grid two-columns">
        <div className="panel">
          <h2>Compliance posture</h2>
          <p>{compliancePosture ? `Frameworks: ${compliancePosture.frameworks.join(', ')}` : 'Loading compliance posture...'}</p>
          <table className="data-table">
            <thead><tr><th>Control</th><th>Status</th><th>Evidence</th></tr></thead>
            <tbody>
              {(compliancePosture?.controls ?? []).map((control) => (
                <tr key={control.control}>
                  <td>{control.control}</td>
                  <td><StatusBadge value={control.status} /></td>
                  <td>{control.evidence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="panel">
          <h2>Security monitoring signals</h2>
          <p>Auth anomalies, replay attacks, and tenant violations detected at runtime.</p>
          {(monitoringSignals ?? []).length === 0 ? <p>No security signals recorded.</p> : (
            <table className="data-table">
              <thead><tr><th>Type</th><th>Severity</th><th>Actor</th><th>Detected</th></tr></thead>
              <tbody>
                {(monitoringSignals ?? []).map((signal) => (
                  <tr key={signal.id}>
                    <td>{signal.type}</td>
                    <td><StatusBadge value={signal.severity} /></td>
                    <td>{signal.actor ?? 'n/a'}</td>
                    <td>{new Date(signal.detectedAt).toLocaleString('en-GB')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      <section className="panel">
        <h2>Message implementation readiness</h2>
        <p>“Limited” means the builder is operational for its documented scenario but still requires partner certification.</p>
        <table className="data-table">
          <thead><tr><th>Message</th><th>Readiness</th><th>Scenario / limitations</th></tr></thead>
          <tbody>
            {(implementations ?? []).map((item) => (
              <tr key={item.documentType}>
                <td>{item.documentType}</td>
                <td>{item.supportLevel}</td>
                <td>{item.scenario}{item.limitations.length ? ` — ${item.limitations.join('; ')}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <ConfirmDialog state={confirmDialog} onClose={() => setConfirmDialog(null)} />
    </div>
  );
}

function GuidedAction({ title, detail, to, search }: { title: string; detail: string; to: string; search: string }) {
  return <Link className="stack-item action-item" to={{ pathname: to, search }}><strong>{title}</strong><span>{detail}</span></Link>;
}
